/**
 * ArtifactManager — copies test artifacts to structured output folders.
 *
 * Handles screenshot/video files from Playwright's result.attachments,
 * organizing them into per-test folders with sanitized names.
 *
 * Uses async copies with a concurrency limiter to avoid blocking the
 * event loop and exhausting file descriptors on large suites.
 */

import { mkdirSync, existsSync, openSync, readSync, closeSync } from 'node:fs';
import { copyFile } from 'node:fs/promises';
import { join, extname } from 'node:path';
import { createHash } from 'node:crypto';
import type { TestArtifacts, VisualComparison } from '@testrelic/core';
import type { VisualCandidate } from './visual-capture.js';
import { readPngDimensions, computeDiffRatio, PNG_HEADER_BYTES } from './visual-metrics.js';

interface Attachment {
  name: string;
  contentType: string;
  path?: string;
  body?: Buffer;
}

// ---------------------------------------------------------------------------
// Async copy queue with concurrency limiter
// ---------------------------------------------------------------------------

const COPY_CONCURRENCY = 20;
let activeCopies = 0;
const pendingCopies: Array<() => void> = [];

/** Queue a file copy with bounded concurrency to prevent fd exhaustion. */
async function enqueueCopy(src: string, dest: string): Promise<boolean> {
  if (activeCopies >= COPY_CONCURRENCY) {
    await new Promise<void>(resolve => pendingCopies.push(resolve));
  }
  activeCopies++;
  try {
    await copyFile(src, dest);
    return true;
  } catch {
    return false;
  } finally {
    activeCopies--;
    if (pendingCopies.length > 0) pendingCopies.shift()!();
  }
}

// Tracks all in-flight copy promises so onEnd can await them
const inflightCopies: Promise<void>[] = [];

/** Wait for all queued artifact copies to complete. Call from reporter onEnd. */
export async function flushPendingArtifactCopies(): Promise<void> {
  if (inflightCopies.length === 0) return;
  await Promise.allSettled(inflightCopies);
  inflightCopies.length = 0;
}

// ---------------------------------------------------------------------------
// Timestamp folder naming
// ---------------------------------------------------------------------------

/**
 * Generate a timestamp-based folder name for the current test run.
 * Format: YYYY-MM-DDTHH-mm-ss (filesystem-safe, sorts chronologically).
 *
 * If the target directory already exists (sub-second collision),
 * appends a numeric suffix: `-1`, `-2`, etc.
 */
export function generateTimestampFolderName(artifactsDir: string): string {
  const now = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  const base = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;

  if (!existsSync(join(artifactsDir, base))) {
    return base;
  }

  // Collision: append numeric suffix
  let suffix = 1;
  while (existsSync(join(artifactsDir, `${base}-${suffix}`))) {
    suffix++;
  }
  return `${base}-${suffix}`;
}

// ---------------------------------------------------------------------------
// Folder name sanitization
// ---------------------------------------------------------------------------

/**
 * Sanitize a test title into a filesystem-safe folder name.
 *
 * Rules:
 * 1. Replace characters not in [a-zA-Z0-9-_ ] with hyphens
 * 2. Replace spaces with hyphens
 * 3. Collapse consecutive hyphens
 * 4. Trim leading/trailing hyphens
 * 5. Truncate to 100 characters
 */
export function sanitizeFolderName(title: string): string {
  let name = title
    .replace(/[^a-zA-Z0-9\-_ ]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');

  if (name.length > 100) {
    name = name.substring(0, 100).replace(/-+$/, '');
  }

  return name || 'unnamed-test';
}

/** Characters of the testId kept as the folder-name disambiguator. */
const ID_SUFFIX_LENGTH = 8;

/**
 * Build the per-test artifact folder name.
 *
 * The sanitized leaf title alone is NOT unique: the same test title can appear
 * in several spec files, in several `describe` blocks, and once per Playwright
 * project (chromium/firefox/webkit). All of those would sanitize to the same
 * folder and race each other writing `video.webm` / `screenshot.png`, leaving
 * every colliding test pointing at one file — so distinct testIds ended up
 * uploading byte-identical artifacts.
 *
 * `testId` (the reporter's stable sha256 of filePath::suiteName::fullTitle)
 * disambiguates them. It is appended as a short suffix so the folder stays
 * human-readable while being unique per test.
 *
 * @param testTitle  Test title used for the readable part of the folder name.
 * @param retryCount Playwright retry index (0 for the first attempt).
 * @param testId     Stable per-test identifier. When omitted the legacy
 *                   title-only naming is used (kept for callers that have no id).
 */
export function buildArtifactFolderName(
  testTitle: string,
  retryCount: number,
  testId?: string,
): string {
  let folderName = sanitizeFolderName(testTitle);

  const suffix = testId ? sanitizeFolderName(testId).substring(0, ID_SUFFIX_LENGTH) : '';
  if (suffix) {
    folderName += `--${suffix}`;
  }

  if (retryCount > 0) {
    folderName += `--retry-${retryCount}`;
  }

  return folderName;
}

// ---------------------------------------------------------------------------
// Artifact copy (non-blocking)
// ---------------------------------------------------------------------------

/**
 * Queue artifact copies from Playwright's temp locations to structured output folders.
 * Returns paths immediately (optimistic) — copies happen asynchronously.
 * Call `flushPendingArtifactCopies()` in onEnd to ensure all copies complete.
 *
 * @param testId Stable per-test id used to disambiguate the destination folder
 *               when two tests share a leaf title (different spec file,
 *               different describe block, or different Playwright project).
 *               Without it those tests collide on one folder and overwrite each
 *               other's `video.webm` / `screenshot.png`.
 * @returns TestArtifacts with relative paths, or null if no artifacts found.
 */
export function copyArtifacts(
  attachments: readonly Attachment[],
  testTitle: string,
  retryCount: number,
  outputDir: string,
  runTimestamp?: string,
  testId?: string,
): TestArtifacts | null {
  const screenshot = attachments.find(
    (a) => a.name === 'screenshot' && a.path,
  );
  const video = attachments.find(
    (a) => a.name === 'video' && a.path,
  );
  const trace = attachments.find(
    (a) => a.name === 'trace' && a.path,
  );

  if (!screenshot && !video && !trace) {
    return null;
  }

  const folderName = buildArtifactFolderName(testTitle, retryCount, testId);

  // When runTimestamp is provided, nest under timestamp folder:
  //   artifacts/<runTimestamp>/<folderName>/
  // Otherwise fall back to legacy flat structure:
  //   artifacts/<folderName>/
  const artifactSegments = runTimestamp
    ? ['artifacts', runTimestamp, folderName]
    : ['artifacts', folderName];
  const artifactDir = join(outputDir, ...artifactSegments);
  const relativeParts = artifactSegments;
  const result: { screenshot?: string; video?: string; trace?: string } = {};

  try {
    mkdirSync(artifactDir, { recursive: true });
  } catch {
    // Cannot create directory — skip artifact capture
    return null;
  }

  if (screenshot?.path) {
    if (existsSync(screenshot.path)) {
      const ext = extname(screenshot.path) || '.png';
      const destName = `screenshot${ext}`;
      const dest = join(artifactDir, destName);
      // Fire-and-forget async copy; tracked for flush in onEnd
      inflightCopies.push(enqueueCopy(screenshot.path, dest).then(() => {}));
      result.screenshot = `${relativeParts.join('/')}/${destName}`;
    }
  }

  if (video?.path) {
    if (existsSync(video.path)) {
      const ext = extname(video.path) || '.webm';
      const destName = `video${ext}`;
      const dest = join(artifactDir, destName);
      inflightCopies.push(enqueueCopy(video.path, dest).then(() => {}));
      result.video = `${relativeParts.join('/')}/${destName}`;
    }
  }

  if (trace?.path) {
    if (existsSync(trace.path)) {
      // Playwright always writes `trace.zip`; keep the extension it gave us
      // rather than assuming, the way the two above do.
      const ext = extname(trace.path) || '.zip';
      const destName = `trace${ext}`;
      const dest = join(artifactDir, destName);
      inflightCopies.push(enqueueCopy(trace.path, dest).then(() => {}));
      result.trace = `${relativeParts.join('/')}/${destName}`;
    }
  }

  if (!result.screenshot && !result.video && !result.trace) {
    return null;
  }

  return result;
}

// ---------------------------------------------------------------------------
// Visual baseline artifacts
// ---------------------------------------------------------------------------

/** Subdirectory holding every visual comparison for one test attempt. */
const VISUAL_DIR = 'visual';

/** Characters of the stem hash appended to keep sanitized names unique. */
const STEM_HASH_LENGTH = 6;

/**
 * Directory name for one snapshot inside a test's `visual/` folder.
 *
 * The snapshot name comes from test code and can contain path separators
 * (`toHaveScreenshot(['checkout', 'cart.png'])` is legal), so it is flattened
 * to a single segment rather than nested — that makes directory traversal
 * structurally impossible instead of merely guarded against. Flattening can
 * collide two different snapshots onto one name, so a short digest of the
 * original stem is appended to keep them apart.
 */
export function buildVisualFolderName(stem: string): string {
  const hash = createHash('sha256').update(stem).digest('hex').substring(0, STEM_HASH_LENGTH);
  return `${sanitizeFolderName(stem)}--${hash}`;
}

/** Read a PNG's dimensions without loading the whole file. */
function readDimensions(filePath: string): { width: number; height: number } | null {
  let fd: number | null = null;
  try {
    fd = openSync(filePath, 'r');
    const header = Buffer.alloc(PNG_HEADER_BYTES);
    const bytesRead = readSync(fd, header, 0, PNG_HEADER_BYTES, 0);
    return readPngDimensions(header.subarray(0, bytesRead));
  } catch {
    return null;
  } finally {
    if (fd !== null) {
      try {
        closeSync(fd);
      } catch {
        // Nothing useful to do if the descriptor will not close.
      }
    }
  }
}

/** The four images a comparison can carry, and what each is called on disk. */
const VISUAL_ROLES = [
  { key: 'expectedPath', dest: 'baseline', field: 'baseline' },
  { key: 'actualPath', dest: 'actual', field: 'actual' },
  { key: 'diffPath', dest: 'diff', field: 'diff' },
  { key: 'previousPath', dest: 'previous', field: 'previous' },
] as const;

/** The DOM snapshots, which live in the same folder as the images they explain. */
const DOM_ROLES = [
  { key: 'baselineDomPath', dest: 'baseline.dom.json', field: 'baselineDom' },
  { key: 'actualDomPath', dest: 'actual.dom.json', field: 'actualDom' },
] as const;

/**
 * Queue copies for every image of one comparison and return its report paths.
 *
 * Mirrors `copyArtifacts`: paths come back immediately and the copies drain
 * through the same bounded queue, flushed by `flushPendingArtifactCopies()`.
 */
function copyOneComparison(
  candidate: VisualCandidate,
  visualDir: string,
  relativePrefix: string,
): VisualComparison | null {
  const folder = buildVisualFolderName(candidate.name);
  const destDir = join(visualDir, folder);

  try {
    mkdirSync(destDir, { recursive: true });
  } catch {
    return null;
  }

  const paths: Record<string, string> = {};
  let copiedAny = false;

  for (const role of VISUAL_ROLES) {
    const src = candidate[role.key];
    if (!src || !existsSync(src)) continue;

    const ext = extname(src) || '.png';
    const destName = `${role.dest}${ext}`;
    inflightCopies.push(enqueueCopy(src, join(destDir, destName)).then(() => {}));
    paths[role.field] = `${relativePrefix}/${VISUAL_DIR}/${folder}/${destName}`;
    copiedAny = true;
  }

  // DOM snapshots are optional: present only where the assertion was written
  // with the SDK's own `expect` and a baseline already existed to compare to.
  const dom = candidate.dom;
  if (dom) {
    for (const role of DOM_ROLES) {
      const src = dom[role.key];
      if (!src || !existsSync(src)) continue;
      inflightCopies.push(enqueueCopy(src, join(destDir, role.dest)).then(() => {}));
      paths[role.field] = `${relativePrefix}/${VISUAL_DIR}/${folder}/${role.dest}`;
    }
  }

  if (!copiedAny) return null;

  // Measured from the baseline where there is one: the ratio has to be a
  // fraction of the image the comparison was judged against, and on a size
  // mismatch the actual is a different size entirely.
  const measured = candidate.expectedPath
    ? readDimensions(candidate.expectedPath)
    : candidate.actualPath
      ? readDimensions(candidate.actualPath)
      : null;
  const ratio = computeDiffRatio(candidate.diffPixels, measured);

  return {
    name: candidate.name,
    status: candidate.status,
    source: candidate.source,
    ...paths,
    ...(measured ? { width: measured.width, height: measured.height } : {}),
    ...(candidate.actualWidth !== undefined ? { actualWidth: candidate.actualWidth } : {}),
    ...(candidate.actualHeight !== undefined ? { actualHeight: candidate.actualHeight } : {}),
    ...(candidate.diffPixels !== undefined ? { diffPixels: candidate.diffPixels } : {}),
    ...(ratio !== undefined ? { diffPixelRatio: ratio } : {}),
    ...(dom
      ? {
          domChanges: dom.changes,
          domChangeCount: dom.total,
          domRootCauses: dom.rootCauses,
          domTruncated: dom.truncated,
          domCaptureBox: dom.captureBox,
        }
      : {}),
  };
}

/**
 * Copy every visual comparison's images into the test's artifact folder.
 *
 * Lands alongside `screenshot.png` / `video.webm` rather than in a tree of its
 * own so that one test attempt stays one directory — the artifact manifest,
 * the report server's `/artifacts/*` route and the run-delete endpoint all
 * keep working unchanged.
 *
 * @returns One entry per comparison whose images were found, or null if none.
 */
export function copyVisualArtifacts(
  candidates: readonly VisualCandidate[],
  testTitle: string,
  retryCount: number,
  outputDir: string,
  runTimestamp?: string,
  testId?: string,
): VisualComparison[] | null {
  if (candidates.length === 0) return null;

  const folderName = buildArtifactFolderName(testTitle, retryCount, testId);
  const artifactSegments = runTimestamp
    ? ['artifacts', runTimestamp, folderName]
    : ['artifacts', folderName];
  const visualDir = join(outputDir, ...artifactSegments, VISUAL_DIR);
  const relativePrefix = artifactSegments.join('/');

  try {
    mkdirSync(visualDir, { recursive: true });
  } catch {
    return null;
  }

  const results: VisualComparison[] = [];
  for (const candidate of candidates) {
    const copied = copyOneComparison(candidate, visualDir, relativePrefix);
    if (copied) results.push(copied);
  }

  return results.length > 0 ? results : null;
}
